import React, { useState } from 'react';
import { Protocol } from '../data/protocols';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
interface ProtocolDetailProps {
  protocol: Protocol;
}
export function ProtocolDetail({
  protocol
}: ProtocolDetailProps) {
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  return <div className="animate-in fade-in duration-300" data-id="element-23">
      {/* Image Zoom Modal */}
      {zoomedImage && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 cursor-zoom-out" onClick={() => setZoomedImage(null)} data-id="element-24">
          <div className="relative max-w-[90vw] max-h-[90vh]" data-id="element-25">
            <img src={zoomedImage} alt="Zoomed technique reference" className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" data-id="element-26" />
            <button onClick={() => setZoomedImage(null)} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors" data-id="element-27">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-28">
                <line x1="18" y1="6" x2="6" y2="18" data-id="element-29"></line>
                <line x1="6" y1="6" x2="18" y2="18" data-id="element-30"></line>
              </svg>
            </button>
          </div>
        </div>}
      {/* Protocol Overview Card */}
      <Card data-id="element-31">
        <Badge data-id="element-32">Protocol</Badge>
        <h2 className="text-[1.1rem] font-semibold text-[#8B1538] mb-2 mt-1" data-id="element-33">
          {protocol.name}
        </h2>
        <p className="text-[0.9rem] text-gray-700 mb-1" data-id="element-34">
          <strong className="font-semibold" data-id="element-35">Category:</strong>{' '}
          {protocol.category}
        </p>
        <p className="text-[0.9rem] text-gray-700 mb-1" data-id="element-36">
          <strong className="font-semibold" data-id="element-37">Key TCM pattern:</strong>{' '}
          {protocol.tcmPattern}
        </p>
        <p className="text-[0.9rem] text-gray-700" data-id="element-38">
          <strong className="font-semibold" data-id="element-39">Clinical picture:</strong>{' '}
          {protocol.indicationSummary}
        </p>
      </Card>

      {/* Point Prescription Card */}
      <Card data-id="element-40">
        <h3 className="text-[0.98rem] font-semibold text-[#8B1538] mb-2" data-id="element-41">
          Point Prescription – Jin's Three Needles
        </h3>
        <div className="overflow-x-auto" data-id="element-42">
          <table className="w-full text-[0.88rem] border-collapse" data-id="element-43">
            <thead data-id="element-44">
              <tr className="bg-[#FEF2F2]" data-id="element-45">
                <th className="border border-red-100 p-2 text-left font-semibold text-[#8B1538]" data-id="element-46">
                  Set
                </th>
                <th className="border border-red-100 p-2 text-left font-semibold text-[#8B1538]" data-id="element-47">
                  Points
                </th>
                <th className="border border-red-100 p-2 text-left font-semibold text-[#8B1538]" data-id="element-48">
                  Side
                </th>
                <th className="border border-red-100 p-2 text-left font-semibold text-[#8B1538]" data-id="element-49">
                  Rationale
                </th>
              </tr>
            </thead>
            <tbody data-id="element-50">
              {protocol.points.map((row, idx) => <tr key={idx} data-id="element-51">
                  <td className="border border-red-100 p-2 align-top text-gray-800" data-id="element-52">
                    {row.pointSet}
                  </td>
                  <td className="border border-red-100 p-2 align-top text-gray-800" data-id="element-53">
                    {row.points}
                  </td>
                  <td className="border border-red-100 p-2 align-top text-gray-800" data-id="element-54">
                    {row.side}
                  </td>
                  <td className="border border-red-100 p-2 align-top text-gray-800" data-id="element-55">
                    {row.note}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <p className="text-[0.8rem] text-gray-500 mt-2 italic" data-id="element-56">
          Adapt depth, angle, and stimulation according to age, constitution,
          and biomedical status.
        </p>
      </Card>

      {/* Needling Parameters Card */}
      <Card data-id="element-57">
        <h3 className="text-[0.98rem] font-semibold text-[#8B1538] mb-2" data-id="element-58">
          Needling Parameters (Jin's Method)
        </h3>
        <ul className="space-y-1.5 text-[0.9rem] text-gray-700" data-id="element-59">
          <li data-id="element-60">
            <strong className="font-semibold" data-id="element-61">Insertion:</strong>{' '}
            {protocol.needling.insertion}
          </li>
          <li data-id="element-62">
            <strong className="font-semibold" data-id="element-63">Depth:</strong>{' '}
            {protocol.needling.depth}
          </li>
          <li data-id="element-64">
            <strong className="font-semibold" data-id="element-65">Retention:</strong>{' '}
            {protocol.needling.retention}
          </li>
          <li data-id="element-66">
            <strong className="font-semibold" data-id="element-67">Manual manipulation:</strong>{' '}
            {protocol.needling.manipulation}
          </li>
          <li data-id="element-68">
            <strong className="font-semibold" data-id="element-69">Withdrawal:</strong>{' '}
            {protocol.needling.withdrawal}
          </li>
        </ul>

        {/* Technique Images */}
        {protocol.images && protocol.images.length > 0 && <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-70">
            <h4 className="text-[0.9rem] font-semibold text-[#8B1538] mb-3" data-id="element-71">
              Technique Reference Images
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-72">
              {protocol.images.map((imageUrl, idx) => <div key={idx} className="rounded-lg overflow-hidden border border-gray-200 shadow-sm cursor-zoom-in group relative" onClick={() => setZoomedImage(imageUrl)} data-id="element-73">
                  <img src={imageUrl} alt={`${protocol.name} technique reference ${idx + 1}`} className="w-full h-auto object-contain bg-white transition-transform duration-200 group-hover:scale-[1.02]" data-id="element-74" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center" data-id="element-75">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-gray-700 text-xs px-2 py-1 rounded-full shadow" data-id="element-76">
                      Click to zoom
                    </span>
                  </div>
                </div>)}
            </div>
          </div>}
      </Card>

      {/* Treatment Course & Remarks Card */}
      <Card data-id="element-77">
        <h3 className="text-[0.98rem] font-semibold text-[#8B1538] mb-2" data-id="element-78">
          Treatment Course & Remarks
        </h3>
        <p className="text-[0.9rem] text-gray-700 mb-2" data-id="element-79">
          <strong className="font-semibold" data-id="element-80">Recommended frequency:</strong>{' '}
          {protocol.frequency}
        </p>
        <p className="text-[0.9rem] text-gray-700" data-id="element-81">{protocol.remarks}</p>
      </Card>
    </div>;
}